import { Injectable } from '@angular/core';
import { CanMatch, Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { FuseNavigationItem } from '@fuse/components/navigation';

@Injectable({
  providedIn: 'root'
})
export class RolesAccessGuard implements CanMatch
{
    /**
      * Constructor
      */
    constructor(
        private _navigationService: NavigationService,
        private _alertService: AlertService,
        private _router: Router
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
      * Can match
      *
      * @param route
      * @param segments
      */
    canMatch(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
    {
        return this._check(segments);
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
      * Check if user can access route based on navigation menu (API-driven permissions)
      *
      * @param segments
      * @private
      */
    private _check(segments: UrlSegment[]): Observable<boolean | UrlTree>
    {
        const targetUrl = this._getTargetUrl(segments);

        // Always allow home and root
        if ( targetUrl === '/home' || targetUrl === '/' )
        {
            return of(true);
        }

        return this._navigationService.get().pipe(
            take(1),
            map((navigation) =>
            {
                const menuItems = navigation?.default || [];
                const hasAccess = this._isRouteInMenu(menuItems, targetUrl);

                if ( !hasAccess )
                {
                    this._alertService.warn('Você não tem permissão para acessar este recurso.', { autoClose: true });
                    return this._router.parseUrl('home');
                }

                return true as boolean | UrlTree;
            })
        );
    }

    /**
      * Extract the full target URL from the current navigation
      *
      * @param segments
      * @private
      */
    private _getTargetUrl(segments: UrlSegment[]): string
    {
        try
        {
            const nav = this._router.getCurrentNavigation();
            const urlSegments = nav?.initialUrl?.root?.children?.['primary']?.segments;
            if ( urlSegments?.length )
            {
                return '/' + urlSegments.map(s => s.path).join('/');
            }
        }
        catch ( e )
        {
            // Fall through to segments fallback
        }

        // Fallback to segments from canMatch (may be partial for parent routes)
        return '/' + segments.map(s => s.path).join('/');
    }

    /**
      * Check if the target URL matches any navigation item link (exact or prefix)
      *
      * @param items
      * @param targetPath
      * @private
      */
    private _isRouteInMenu(items: FuseNavigationItem[], targetPath: string): boolean
    {
        for ( const item of items )
        {
            if ( item.link )
            {
                const navLink = item.link.startsWith('/') ? item.link : '/' + item.link;
                if ( targetPath === navLink || targetPath.startsWith(navLink + '/') )
                {
                    return true;
                }
            }

            if ( item.children && this._isRouteInMenu(item.children, targetPath) )
            {
                return true;
            }
        }
        return false;
    }
}
